import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle,
  Download,
  RefreshCw,
  Search,
  Send,
  Trash2,
  X,
  XCircle,
  Zap,
} from 'lucide-react';
import { toCsv, downloadCsv } from '@/lib/csv';
import { supabase, invokeFunction } from '@/lib/supabase';
import type {
  Order,
  OrderStatus,
  Profile,
  WorkbookProfile,
  GenerationMetadata,
  GenerationWarning,
} from '@ellie/shared';
import { CostIndicatorModal } from '@/components/CostIndicatorModal';
import { useActiveProvider } from '@/hooks/useActiveProvider';
import { logAudit } from '@/lib/audit';
import { EmailPreviewModal } from '@/components/EmailPreviewModal';
import { Pagination } from '@/components/Pagination';
import { OrderJourneyDrawer } from '@/components/OrderJourneyDrawer';

const PAGE_SIZE = 25;

const ALL_STATUSES: OrderStatus[] = [
  'preview',
  'pending',
  'paid',
  'generating',
  'review',
  'approved',
  'delivering',
  'delivered',
  'rejected',
  'failed',
  'refunded',
];

/** Statuses where an order can be deleted (terminal / dead-end states). */
const DELETABLE_STATUSES: OrderStatus[] = ['preview', 'pending', 'failed', 'rejected', 'refunded'];

const STATUS_COLORS: Record<OrderStatus, string> = {
  preview: 'bg-indigo-100 text-indigo-700',
  pending: 'bg-gray-100 text-gray-700',
  paid: 'bg-blue-100 text-blue-700',
  generating: 'bg-purple-100 text-purple-700',
  review: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  delivering: 'bg-orange-100 text-orange-700',
  delivered: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-500',
};

// ---------------------------------------------------------------------------
// Bulk operation types
// ---------------------------------------------------------------------------

type BulkAction = 'approve' | 'generate' | 'deliver' | 'delete';

/** Which order statuses are valid for each bulk action. */
const VALID_STATUSES_FOR_ACTION: Record<BulkAction, Set<OrderStatus>> = {
  approve: new Set(['review']),
  generate: new Set(['paid', 'rejected', 'preview', 'failed']),
  deliver: new Set(['approved', 'delivering']),
  delete: new Set(['pending', 'failed', 'rejected', 'refunded']),
};

const BULK_ACTION_LABELS: Record<BulkAction, string> = {
  approve: 'Approve',
  generate: 'Generate',
  deliver: 'Deliver',
  delete: 'Delete',
};

const BULK_ACTION_PROGRESS_LABELS: Record<BulkAction, string> = {
  approve: 'Approving',
  generate: 'Generating',
  deliver: 'Delivering',
  delete: 'Deleting',
};

interface BulkOperation {
  action: BulkAction;
  orderIds: string[];
  phase: 'confirm' | 'running' | 'done';
  completed: number;
  succeeded: number;
  failed: number;
  errors: { orderId: string; message: string }[];
}

// ---------------------------------------------------------------------------
// Shared types & helpers
// ---------------------------------------------------------------------------

interface OrderWithProfile extends Order {
  profiles: Pick<Profile, 'full_name' | 'email'> | null;
  workbooks:
    | {
        generation_metadata: GenerationMetadata | null;
        generation_warnings: GenerationWarning[];
      }[]
    | null;
}

function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function truncateId(id: string): string {
  return id.slice(0, 8);
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') ?? 'all';
  const page = Number(searchParams.get('page') ?? '1');

  const [orders, setOrders] = useState<OrderWithProfile[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());

  // Active AI provider (from admin settings)
  const { provider: activeProvider, model: activeModel } = useActiveProvider();

  // Cost indicator modal state
  const [costModalOrder, setCostModalOrder] = useState<OrderWithProfile | null>(null);
  const [costModalProfile, setCostModalProfile] = useState<WorkbookProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Deliver order state
  const [deliveringIds, setDeliveringIds] = useState<Set<string>>(new Set());

  // Email preview modal state
  const [previewOrder, setPreviewOrder] = useState<OrderWithProfile | null>(null);

  // Delete order state
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteModalOrder, setDeleteModalOrder] = useState<OrderWithProfile | null>(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkOperation, setBulkOperation] = useState<BulkOperation | null>(null);

  // Clear selection when filter changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter]);

  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  // Cancel generation state
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());
  const [cancelModalOrder, setCancelModalOrder] = useState<OrderWithProfile | null>(null);

  // PDF download state
  const [downloadingPdfIds, setDownloadingPdfIds] = useState<Set<string>>(new Set());

  // Recover stuck orders state
  const [recovering, setRecovering] = useState(false);

  // Journey drawer state
  const [journeyOrderId, setJourneyOrderId] = useState<string | null>(null);

  function handleSearchChange(value: string) {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(value), 250);
  }

  function clearSearch() {
    setSearchInput('');
    setSearchQuery('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const filteredOrders = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return orders;
    return orders.filter((order) => {
      const name = order.profiles?.full_name?.toLowerCase() ?? '';
      const email = order.profiles?.email?.toLowerCase() ?? '';
      const id = order.id.toLowerCase();
      return id.includes(q) || name.includes(q) || email.includes(q);
    });
  }, [orders, searchQuery]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);

    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase
      .from('orders')
      .select(
        '*, profiles(full_name, email), workbooks(generation_metadata, generation_warnings)',
        {
          count: 'exact',
        },
      )
      .order('created_at', { ascending: false })
      .range(from, to);

    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }

    const { data, count, error } = await query;

    if (error) {
      console.error('Failed to fetch orders:', error.message);
    }

    setOrders((data as OrderWithProfile[]) ?? []);
    setTotalCount(count ?? 0);
    setSelectedIds(new Set());
    setLoading(false);
  }, [statusFilter, page]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  function handleStatusChange(status: string) {
    // Reset to page 1 when changing filters
    if (status === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ status });
    }
  }

  function handlePageChange(newPage: number) {
    const params: Record<string, string> = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (newPage > 1) params.page = String(newPage);
    setSearchParams(params);
  }

  // ---------------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------------

  function toggleSelection(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds((prev) => {
      if (prev.size === orders.length) {
        return new Set();
      }
      return new Set(orders.map((o) => o.id));
    });
  }

  const allSelected = orders.length > 0 && selectedIds.size === orders.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < orders.length;

  /**
   * Compute which bulk actions are valid for the current selection.
   * An action is available only when ALL selected orders have a status
   * that is valid for that action.
   */
  const availableBulkActions = useMemo<BulkAction[]>(() => {
    if (selectedIds.size === 0) return [];

    const selectedOrders = orders.filter((o) => selectedIds.has(o.id));
    const actions: BulkAction[] = [];

    for (const action of ['approve', 'generate', 'deliver', 'delete'] as BulkAction[]) {
      const validStatuses = VALID_STATUSES_FOR_ACTION[action];
      if (selectedOrders.every((o) => validStatuses.has(o.status))) {
        actions.push(action);
      }
    }

    return actions;
  }, [selectedIds, orders]);

  // ---------------------------------------------------------------------------
  // Bulk Operations
  // ---------------------------------------------------------------------------

  function startBulkAction(action: BulkAction) {
    setBulkOperation({
      action,
      orderIds: [...selectedIds],
      phase: 'confirm',
      completed: 0,
      succeeded: 0,
      failed: 0,
      errors: [],
    });
  }

  function closeBulkOperation() {
    const wasDone = bulkOperation?.phase === 'done';
    setBulkOperation(null);
    if (wasDone) {
      setSelectedIds(new Set());
    }
  }

  async function executeBulkAction() {
    if (!bulkOperation || bulkOperation.phase !== 'confirm') return;

    const { action, orderIds } = bulkOperation;
    const total = orderIds.length;

    // Transition to running phase
    setBulkOperation({
      action,
      orderIds,
      phase: 'running',
      completed: 0,
      succeeded: 0,
      failed: 0,
      errors: [],
    });

    let succeeded = 0;
    let failed = 0;
    const errors: { orderId: string; message: string }[] = [];

    if (action === 'approve') {
      // Batch update — single query for all orders
      try {
        const { error } = await supabase
          .from('orders')
          .update({
            status: 'approved',
            approved_at: new Date().toISOString(),
          })
          .in('id', orderIds);

        if (error) throw error;

        succeeded = total;
        setOrders((prev) =>
          prev.map((o) =>
            orderIds.includes(o.id)
              ? {
                  ...o,
                  status: 'approved' as OrderStatus,
                  approved_at: new Date().toISOString(),
                }
              : o,
          ),
        );
      } catch (err) {
        failed = total;
        errors.push({
          orderId: 'batch',
          message: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    } else if (action === 'delete') {
      // Batch delete — single query for all orders
      try {
        const { error } = await supabase.from('orders').delete().in('id', orderIds);

        if (error) throw error;

        succeeded = total;
        setOrders((prev) => prev.filter((o) => !orderIds.includes(o.id)));
      } catch (err) {
        failed = total;
        errors.push({
          orderId: 'batch',
          message: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    } else {
      // Sequential processing for generate / deliver (each triggers an edge function)
      const fnName = action === 'generate' ? 'trigger-generation' : 'deliver-workbook';
      const nextStatus: OrderStatus = action === 'generate' ? 'generating' : 'delivering';

      for (let i = 0; i < orderIds.length; i++) {
        const orderId = orderIds[i];

        try {
          const { data, error: fnError } = await invokeFunction<{ error?: string }>(fnName, {
            body: { order_id: orderId },
          });

          if (fnError) throw new Error(fnError.message ?? 'Request failed');
          if (data?.error) throw new Error(data.error);

          succeeded++;
          setOrders((prev) =>
            prev.map((o) => (o.id === orderId ? { ...o, status: nextStatus } : o)),
          );
        } catch (err) {
          failed++;
          errors.push({
            orderId,
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }

        // Update progress after each item
        setBulkOperation({
          action,
          orderIds,
          phase: 'running',
          completed: i + 1,
          succeeded,
          failed,
          errors: [...errors],
        });
      }
    }

    // Final result
    setBulkOperation({
      action,
      orderIds,
      phase: 'done',
      completed: total,
      succeeded,
      failed,
      errors,
    });
  }

  // ---------------------------------------------------------------------------
  // Single order actions (existing)
  // ---------------------------------------------------------------------------

  /** Open the cost indicator modal — fetches the user's workbook profile first. */
  async function handleGenerateClick(order: OrderWithProfile) {
    if (!order.user_id) {
      alert('This order has no linked user. Cannot generate.');
      return;
    }

    setLoadingProfile(true);
    setCostModalOrder(order);

    try {
      const { data, error } = await supabase
        .from('workbook_profiles')
        .select('*')
        .eq('user_id', order.user_id)
        .eq('is_active', true)
        .limit(1)
        .single();

      if (error || !data) {
        throw new Error(error?.message ?? 'No active workbook profile found');
      }

      setCostModalProfile(data as WorkbookProfile);
    } catch (err) {
      console.error('Failed to fetch workbook profile:', err);
      alert(
        `Failed to load workbook profile: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
      setCostModalOrder(null);
    } finally {
      setLoadingProfile(false);
    }
  }

  function handleCostModalCancel() {
    setCostModalOrder(null);
    setCostModalProfile(null);
  }

  /** Proceed with generation after the admin confirms in the modal. */
  async function handleCostModalConfirm() {
    const order = costModalOrder;
    if (!order) return;

    // Close modal immediately
    setCostModalOrder(null);
    setCostModalProfile(null);

    setGeneratingIds((prev) => new Set(prev).add(order.id));

    try {
      const { data, error: fnError } = await invokeFunction<{ error?: string }>(
        'trigger-generation',
        { body: { order_id: order.id } },
      );

      if (fnError) {
        throw new Error(fnError.message ?? 'Request failed');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // Optimistically update the row
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, status: 'generating' as OrderStatus } : o)),
      );

      logAudit({
        action: 'generate_workbook',
        resourceType: 'order',
        resourceId: order.id,
        metadata: { previous_status: order.status },
      });
    } catch (err) {
      console.error('Generation trigger failed:', err);
      alert(
        `Failed to trigger generation: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    } finally {
      setGeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
    }
  }

  /** Delete an order (pending/failed/rejected/refunded) after admin confirms. */
  async function handleDeleteConfirm() {
    const order = deleteModalOrder;
    if (!order) return;

    // Close modal immediately
    setDeleteModalOrder(null);

    setDeletingIds((prev) => new Set(prev).add(order.id));

    try {
      const { error } = await supabase.from('orders').delete().eq('id', order.id);

      if (error) {
        throw new Error(error.message);
      }

      // Remove from local state
      setOrders((prev) => prev.filter((o) => o.id !== order.id));

      logAudit({
        action: 'delete_order',
        resourceType: 'order',
        resourceId: order.id,
        metadata: {
          previous_status: order.status,
          customer_email: order.profiles?.email ?? null,
        },
      });

      // Remove from selection if selected
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
    } catch (err) {
      console.error('Failed to delete order:', err);
      alert(`Failed to delete order: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
    }
  }

  /** Cancel a stuck generation — resets order back to paid so it can be retried. */
  async function handleCancelConfirm() {
    const order = cancelModalOrder;
    if (!order) return;

    // Close modal immediately
    setCancelModalOrder(null);

    setCancellingIds((prev) => new Set(prev).add(order.id));

    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'paid', generated_at: null })
        .eq('id', order.id);

      if (error) {
        throw new Error(error.message);
      }

      // Optimistically update local state
      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id ? { ...o, status: 'paid' as OrderStatus, generated_at: null } : o,
        ),
      );
    } catch (err) {
      console.error('Failed to cancel generation:', err);
      alert(`Failed to cancel generation: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setCancellingIds((prev) => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
    }
  }

  /** Open email preview modal before delivering. */
  function handleDeliverClick(order: OrderWithProfile) {
    setPreviewOrder(order);
  }

  /** Proceed with delivery after admin confirms from the preview modal. */
  async function handleDeliverConfirm() {
    const order = previewOrder;
    if (!order) return;

    setDeliveringIds((prev) => new Set(prev).add(order.id));

    try {
      const { data, error: fnError } = await invokeFunction<{ error?: string }>(
        'deliver-workbook',
        { body: { order_id: order.id } },
      );

      if (fnError) {
        throw new Error(fnError.message ?? 'Request failed');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // Close modal and optimistically update the row
      setPreviewOrder(null);
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, status: 'delivering' as OrderStatus } : o)),
      );
    } catch (err) {
      console.error('Delivery trigger failed:', err);
      alert(`Failed to trigger delivery: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeliveringIds((prev) => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
    }
  }

  /** Retry generation for a failed order — calls trigger-generation which handles
   *  cleanup of old workbook and retry_count increment. */
  async function handleRetryGeneration(order: OrderWithProfile) {
    setGeneratingIds((prev) => new Set(prev).add(order.id));

    try {
      const { data, error: fnError } = await invokeFunction<{ error?: string }>(
        'trigger-generation',
        { body: { order_id: order.id } },
      );

      if (fnError) {
        throw new Error(fnError.message ?? 'Request failed');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // Optimistically update the row
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, status: 'generating' as OrderStatus } : o)),
      );

      logAudit({
        action: 'retry_generation',
        resourceType: 'order',
        resourceId: order.id,
        metadata: {
          previous_status: order.status,
          retry_count: (order.retry_count ?? 0) + 1,
        },
      });
    } catch (err) {
      console.error('Retry generation failed:', err);
      alert(`Failed to retry generation: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setGeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
    }
  }

  function handleExportCsv() {
    const csv = toCsv(orders, [
      { header: 'Order ID', accessor: (o) => o.id },
      { header: 'Customer', accessor: (o) => o.profiles?.full_name ?? '' },
      { header: 'Email', accessor: (o) => o.profiles?.email ?? '' },
      { header: 'Status', accessor: (o) => o.status },
      { header: 'Delivery', accessor: (o) => o.delivery_method },
      {
        header: 'Amount',
        accessor: (o) =>
          o.status !== 'preview' && o.amount_cents > 0 ? (o.amount_cents / 100).toFixed(2) : '',
      },
      { header: 'Currency', accessor: (o) => o.currency.toUpperCase() },
      {
        header: 'AI Cost (USD)',
        accessor: (o) => {
          const meta = o.workbooks?.[0]?.generation_metadata;
          return meta ? meta.estimated_cost_usd.toFixed(2) : '';
        },
      },
      { header: 'Created', accessor: (o) => o.created_at },
    ]);

    downloadCsv(csv, 'orders');
  }

  // ---------------------------------------------------------------------------
  // Recover stuck orders
  // ---------------------------------------------------------------------------

  async function handleRecoverStuckOrders() {
    setRecovering(true);
    try {
      const { data, error } = await invokeFunction<{
        recovered: number;
        total_stuck: number;
        orders: string[];
        error?: string;
      }>('recover-stuck-orders', {});

      if (error) throw new Error(error.message ?? 'Request failed');
      if (data?.error) throw new Error(data.error);

      if (data && data.recovered > 0) {
        alert(
          `Recovered ${data.recovered} stuck order(s). They have been reset to 'failed' and can be retried.`,
        );
        logAudit({
          action: 'recover_stuck_orders',
          resourceType: 'order',
          metadata: { recovered: data.recovered, order_ids: data.orders },
        });
        fetchOrders();
      } else {
        alert('No stuck orders found.');
      }
    } catch (err) {
      alert(
        `Failed to recover stuck orders: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    } finally {
      setRecovering(false);
    }
  }

  // ---------------------------------------------------------------------------
  // PDF download
  // ---------------------------------------------------------------------------

  /** Download the production PDF for an order.
   *  If the order already has a cached `pdf_storage_path`, create a signed URL
   *  directly from storage (no re-render needed). Otherwise invoke `render-pdf`
   *  to generate it first.
   */
  async function handleDownloadPdf(order: OrderWithProfile) {
    setDownloadingPdfIds((prev) => new Set(prev).add(order.id));

    try {
      let signedUrl: string;

      if (order.pdf_storage_path) {
        // PDF was already rendered — grab a signed URL from storage directly
        const { data, error } = await supabase.storage
          .from('workbooks')
          .createSignedUrl(order.pdf_storage_path, 60 * 60);

        if (error || !data?.signedUrl) {
          throw new Error(error?.message ?? 'Failed to create signed URL');
        }

        signedUrl = data.signedUrl;
      } else {
        // No cached PDF yet — render via the edge function
        const { data, error: fnError } = await invokeFunction<{
          signed_url: string;
        }>('render-pdf', { body: { order_id: order.id } });

        if (fnError) {
          throw new Error(fnError.message ?? 'PDF rendering failed');
        }

        signedUrl = data.signed_url;

        // Optimistically update local state so subsequent clicks use the cache
        setOrders((prev) =>
          prev.map((o) =>
            o.id === order.id ? { ...o, pdf_storage_path: `${order.id}/ellie-workbook.pdf` } : o,
          ),
        );
      }

      // Fetch as blob — browsers ignore `download` on cross-origin URLs
      const pdfResponse = await fetch(signedUrl);
      if (!pdfResponse.ok) throw new Error('Failed to download PDF');
      const blob = await pdfResponse.blob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ellie-workbook.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      alert(`PDF download failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDownloadingPdfIds((prev) => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl text-text-primary">Orders</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRecoverStuckOrders}
            disabled={recovering}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${recovering ? 'animate-spin' : ''}`} />
            {recovering ? 'Recovering…' : 'Recover Stuck'}
          </button>
          <button
            onClick={handleExportCsv}
            disabled={orders.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="mb-6 flex flex-wrap gap-2">
        <FilterPill
          label="All"
          active={statusFilter === 'all'}
          onClick={() => handleStatusChange('all')}
        />
        {ALL_STATUSES.map((status) => (
          <FilterPill
            key={status}
            label={status}
            active={statusFilter === status}
            onClick={() => handleStatusChange(status)}
          />
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search by name, email, or order ID…"
          className="w-full rounded-lg border border-border bg-white py-2 pl-9 pr-9 text-sm text-text-primary placeholder:text-text-light focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
        {searchInput && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Bulk action toolbar — visible when orders are selected */}
      {selectedIds.size > 0 && !bulkOperation && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-brand/20 bg-brand/5 px-4 py-3">
          <span className="text-sm font-medium text-text-primary">
            {selectedIds.size} order{selectedIds.size !== 1 ? 's' : ''} selected
          </span>

          <div className="flex items-center gap-2">
            {availableBulkActions.includes('approve') && (
              <button
                onClick={() => startBulkAction('approve')}
                className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700"
              >
                <CheckCircle className="h-3 w-3" />
                Approve
              </button>
            )}
            {availableBulkActions.includes('generate') && (
              <button
                onClick={() => startBulkAction('generate')}
                className="inline-flex items-center gap-1 rounded-md bg-purple-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-purple-700"
              >
                <Zap className="h-3 w-3" />
                Generate
              </button>
            )}
            {availableBulkActions.includes('deliver') && (
              <button
                onClick={() => startBulkAction('deliver')}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700"
              >
                <Send className="h-3 w-3" />
                Deliver
              </button>
            )}
            {availableBulkActions.includes('delete') && (
              <button
                onClick={() => startBulkAction('delete')}
                className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700"
              >
                <Trash2 className="h-3 w-3" />
                Delete
              </button>
            )}
          </div>

          {availableBulkActions.length === 0 && (
            <span className="text-xs text-text-muted">No actions available for this selection</span>
          )}

          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto inline-flex items-center gap-1 text-xs text-text-muted transition-colors hover:text-text-primary"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <p className="py-12 text-center text-sm text-text-muted">
          {searchQuery
            ? `No orders matching "${searchQuery}"`
            : `No orders found${statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}`}
          .
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-gray-50 text-xs font-medium uppercase tracking-wider text-text-muted">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 cursor-pointer rounded border-gray-300 text-brand accent-brand"
                  />
                </th>
                <th className="px-4 py-3">Order ID</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Delivery</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">AI Cost</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr
                  key={order.id}
                  className={`border-b border-border-light transition-colors last:border-b-0 hover:bg-gray-50 ${
                    selectedIds.has(order.id) ? 'bg-brand/5' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(order.id)}
                      onChange={() => toggleSelection(order.id)}
                      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-brand accent-brand"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setJourneyOrderId(order.id)}
                      className="font-mono text-xs text-brand hover:underline"
                      title="View order journey"
                    >
                      {truncateId(order.id)}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-text-primary">
                      {order.profiles?.full_name ?? 'Unknown'}
                    </p>
                    <p className="text-xs text-text-muted">{order.profiles?.email ?? ''}</p>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-4 py-3 capitalize text-text-muted">{order.delivery_method}</td>
                  <td className="px-4 py-3 text-text-primary">
                    {order.status !== 'preview' && order.amount_cents > 0
                      ? formatCurrency(order.amount_cents, order.currency)
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <AiCostCell workbooks={order.workbooks} />
                  </td>
                  <td className="px-4 py-3 text-text-muted">{formatDate(order.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {(order.status === 'review' ||
                        order.status === 'approved' ||
                        order.status === 'delivering' ||
                        order.status === 'delivered') && (
                        <button
                          onClick={() => handleDownloadPdf(order)}
                          disabled={downloadingPdfIds.has(order.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {downloadingPdfIds.has(order.id) ? (
                            <>
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                              PDF…
                            </>
                          ) : (
                            <>
                              <Download className="h-3 w-3" />
                              PDF
                            </>
                          )}
                        </button>
                      )}
                      {(order.status === 'paid' || order.status === 'rejected') && (
                        <button
                          onClick={() => handleGenerateClick(order)}
                          disabled={generatingIds.has(order.id) || loadingProfile}
                          className="inline-flex items-center gap-1 rounded-md bg-purple-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {generatingIds.has(order.id) ? (
                            <>
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                              Generating…
                            </>
                          ) : (
                            <>
                              <Zap className="h-3 w-3" />
                              {order.status === 'rejected' ? 'Regenerate' : 'Generate'}
                            </>
                          )}
                        </button>
                      )}
                      {order.status === 'generating' && (
                        <button
                          onClick={() => setCancelModalOrder(order)}
                          disabled={cancellingIds.has(order.id)}
                          className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {cancellingIds.has(order.id) ? (
                            <>
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                              Cancelling…
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3" />
                              Cancel
                            </>
                          )}
                        </button>
                      )}
                      {order.status === 'approved' && (
                        <button
                          onClick={() => handleDeliverClick(order)}
                          disabled={deliveringIds.has(order.id)}
                          className="inline-flex items-center gap-1 rounded-md bg-green-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {deliveringIds.has(order.id) ? (
                            <>
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                              Delivering…
                            </>
                          ) : (
                            <>
                              <Send className="h-3 w-3" />
                              Deliver
                            </>
                          )}
                        </button>
                      )}
                      {order.status === 'failed' && (
                        <button
                          onClick={() => handleRetryGeneration(order)}
                          disabled={generatingIds.has(order.id)}
                          className="inline-flex items-center gap-1 rounded-md bg-purple-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                          title={`Retry generation (attempt ${(order.retry_count ?? 0) + 1}/3)`}
                        >
                          {generatingIds.has(order.id) ? (
                            <>
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                              Retrying…
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-3 w-3" />
                              Retry Generation
                            </>
                          )}
                        </button>
                      )}
                      {DELETABLE_STATUSES.includes(order.status) && (
                        <button
                          onClick={() => setDeleteModalOrder(order)}
                          disabled={deletingIds.has(order.id)}
                          className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {deletingIds.has(order.id) ? (
                            <>
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                              Deleting…
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-3 w-3" />
                              Delete
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Cost indicator modal */}
      {costModalOrder && costModalProfile && (
        <CostIndicatorModal
          order={costModalOrder}
          profile={costModalProfile}
          onConfirm={handleCostModalConfirm}
          onCancel={handleCostModalCancel}
          isRegenerating={costModalOrder.status === 'rejected'}
          provider={activeProvider}
          model={activeModel}
        />
      )}

      {/* Email preview modal */}
      {previewOrder && (
        <EmailPreviewModal
          recipientName={previewOrder.profiles?.full_name ?? ''}
          recipientEmail={previewOrder.profiles?.email ?? ''}
          onConfirm={handleDeliverConfirm}
          onCancel={() => setPreviewOrder(null)}
          isDelivering={deliveringIds.has(previewOrder.id)}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteModalOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-text-primary">Delete Order</h2>
            <p className="mb-4 text-sm text-text-muted">
              Are you sure you want to delete this {deleteModalOrder.status} order?
            </p>

            <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-text-muted">Order ID</span>
                <span className="font-mono text-text-primary">
                  {truncateId(deleteModalOrder.id)}
                </span>
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-text-muted">Customer</span>
                <span className="text-text-primary">
                  {deleteModalOrder.profiles?.full_name ?? 'Unknown'}
                </span>
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-text-muted">Amount</span>
                <span className="text-text-primary">
                  {formatCurrency(deleteModalOrder.amount_cents, deleteModalOrder.currency)}
                </span>
              </div>
            </div>

            <p className="mb-4 text-xs font-medium text-red-600">This action cannot be undone.</p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModalOrder(null)}
                className="rounded-md px-4 py-2 text-sm font-medium text-text-muted transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
              >
                Delete Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk operation modal */}
      {bulkOperation && (
        <BulkOperationModal
          operation={bulkOperation}
          onConfirm={executeBulkAction}
          onClose={closeBulkOperation}
        />
      )}

      {/* Cancel generation confirmation modal */}
      {cancelModalOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-text-primary">Cancel Generation</h2>
            <p className="mb-4 text-sm text-text-muted">
              Are you sure you want to cancel this generation? The order will be reset to paid
              status so you can retry.
            </p>

            <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-text-muted">Order ID</span>
                <span className="font-mono text-text-primary">
                  {truncateId(cancelModalOrder.id)}
                </span>
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-text-muted">Customer</span>
                <span className="text-text-primary">
                  {cancelModalOrder.profiles?.full_name ?? 'Unknown'}
                </span>
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-text-muted">Amount</span>
                <span className="text-text-primary">
                  {formatCurrency(cancelModalOrder.amount_cents, cancelModalOrder.currency)}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setCancelModalOrder(null)}
                className="rounded-md px-4 py-2 text-sm font-medium text-text-muted transition-colors hover:bg-gray-100"
              >
                Keep Generating
              </button>
              <button
                onClick={handleCancelConfirm}
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
              >
                Cancel Generation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay while fetching profile */}
      {loadingProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-purple-600 border-t-transparent" />
              <span className="text-sm text-text-muted">Loading cost estimate…</span>
            </div>
          </div>
        </div>
      )}

      {/* Order journey drawer */}
      {journeyOrderId && (
        <OrderJourneyDrawer orderId={journeyOrderId} onClose={() => setJourneyOrderId(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter Pill
// ---------------------------------------------------------------------------

interface FilterPillProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function FilterPill({ label, active, onClick }: FilterPillProps) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors ${
        active ? 'bg-brand text-white' : 'bg-gray-100 text-text-muted hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Status Badge
// ---------------------------------------------------------------------------

interface StatusBadgeProps {
  status: OrderStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const color = STATUS_COLORS[status];
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${color}`}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// AI Cost Cell
// ---------------------------------------------------------------------------

interface AiCostCellProps {
  workbooks:
    | {
        generation_metadata: GenerationMetadata | null;
        generation_warnings: GenerationWarning[];
      }[]
    | null;
}

function AiCostCell({ workbooks }: AiCostCellProps) {
  const metadata = workbooks?.[0]?.generation_metadata;
  const warnings = workbooks?.[0]?.generation_warnings ?? [];

  if (!metadata) {
    return <span className="text-xs text-text-light">—</span>;
  }

  const blocksInfo =
    metadata.blocks_succeeded != null && metadata.blocks_total != null
      ? ` · ${metadata.blocks_succeeded}/${metadata.blocks_total} blocks`
      : '';

  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="cursor-default text-xs font-medium text-text-primary"
        title={`${metadata.input_tokens.toLocaleString()} in / ${metadata.output_tokens.toLocaleString()} out · ${metadata.api_calls} API calls · ${(metadata.duration_ms / 1000).toFixed(1)}s${blocksInfo}`}
      >
        ${metadata.estimated_cost_usd.toFixed(2)}
      </span>
      {warnings.length > 0 && (
        <span
          className="cursor-default text-amber-500"
          title={`${warnings.length} warning(s): ${warnings.map((w) => w.component).join(', ')}`}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Bulk Operation Modal
// ---------------------------------------------------------------------------

const BULK_CONFIRM_COLORS: Record<BulkAction, string> = {
  approve: 'bg-green-600 hover:bg-green-700',
  generate: 'bg-purple-600 hover:bg-purple-700',
  deliver: 'bg-emerald-600 hover:bg-emerald-700',
  delete: 'bg-red-600 hover:bg-red-700',
};

const BULK_DESCRIPTIONS: Record<BulkAction, string> = {
  approve: 'This will approve all selected orders, making them ready for delivery.',
  generate:
    'This will trigger AI workbook generation for each selected order. This incurs API costs.',
  deliver: 'This will trigger delivery for each selected order.',
  delete: 'This will permanently delete all selected orders.',
};

interface BulkOperationModalProps {
  operation: BulkOperation;
  onConfirm: () => void;
  onClose: () => void;
}

function BulkOperationModal({ operation, onConfirm, onClose }: BulkOperationModalProps) {
  const { action, phase, completed, succeeded, failed, errors } = operation;
  const total = operation.orderIds.length;
  const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        {/* Confirmation phase */}
        {phase === 'confirm' && (
          <>
            <h2 className="mb-2 text-lg font-semibold text-text-primary">
              {BULK_ACTION_LABELS[action]} {total} Order
              {total !== 1 ? 's' : ''}
            </h2>
            <p className="mb-4 text-sm text-text-muted">{BULK_DESCRIPTIONS[action]}</p>
            {action === 'delete' && (
              <p className="mb-4 text-xs font-medium text-red-600">This action cannot be undone.</p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="rounded-md px-4 py-2 text-sm font-medium text-text-muted transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className={`rounded-md px-4 py-2 text-sm font-medium text-white transition-colors ${BULK_CONFIRM_COLORS[action]}`}
              >
                {BULK_ACTION_LABELS[action]} {total} Order
                {total !== 1 ? 's' : ''}
              </button>
            </div>
          </>
        )}

        {/* Progress phase */}
        {phase === 'running' && (
          <>
            <h2 className="mb-4 text-lg font-semibold text-text-primary">
              {BULK_ACTION_PROGRESS_LABELS[action]} Orders…
            </h2>
            <div className="mb-2 h-2 overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-brand transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="text-sm text-text-muted">
              {completed} of {total} completed
            </p>
            {failed > 0 && <p className="mt-1 text-sm text-red-600">{failed} failed</p>}
          </>
        )}

        {/* Results phase */}
        {phase === 'done' && (
          <>
            <h2 className="mb-2 text-lg font-semibold text-text-primary">
              Bulk {BULK_ACTION_LABELS[action]} Complete
            </h2>
            <div className="mb-4 space-y-1 text-sm">
              {succeeded > 0 && <p className="text-green-600">{succeeded} succeeded</p>}
              {failed > 0 && <p className="text-red-600">{failed} failed</p>}
            </div>
            {errors.length > 0 && (
              <div className="mb-4 max-h-32 overflow-y-auto rounded-lg bg-red-50 p-3 text-xs text-red-700">
                {errors.map((e, i) => (
                  <p key={i}>
                    {e.orderId === 'batch' ? 'Error' : truncateId(e.orderId)}: {e.message}
                  </p>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand/90"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
